/**
 * Desktop apps that hold their own key.
 *
 * A list and a revoke button. **Approving happens in the app**, and this page
 * deliberately cannot do it any more.
 *
 * ── Why the Approve button is gone ──────────────────────────────────────────
 *
 * It was already the wrong shape and had become actively wrong twice over.
 *
 * It approved with `wrapKeyForRecipient(d.publicKey, masterKey)` and **never
 * branched on `d.scope`** — so approving a pending app-scoped row here handed
 * that app the entire vault, which is precisely what migration 0026 exists to
 * prevent. The server cannot catch it: it stores an opaque string and can read
 * neither what is inside nor what it opens.
 *
 * And it could not finish the job anyway. An app-scoped device needs a root
 * folder before it can be approved — `approve` stores the key to that very node
 * — and nothing here mints one. The footnote under the button said "create this
 * app's folder before approving it", which named an action this page does not
 * have.
 *
 * `AuthoriseApp.tsx` does both correctly: it mints the folder, and it wraps that
 * folder's key rather than the master key. An app reaches it by opening
 * `/authorise#dk=…` itself, with its public key in the fragment, which the
 * server never sees. Suite links its siblings over a local socket without any
 * page at all (`docs/proposals/auto-link.md`), and Relay can do the ceremony
 * itself from the account password.
 *
 * So a pending row here has nothing useful to offer but the one thing this page
 * is still the right home for: refusing it.
 *
 * ── What a row still says ───────────────────────────────────────────────────
 *
 * What the device would be able to read — the whole vault, or one app's own
 * folder. There is no fingerprint: migration 0026 replaced that comparison with
 * the URL fragment, and this page stopped rendering one before the apps stopped
 * asking people to compare it.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, type DeviceWire } from '../api';
import { ds } from '../ds';
import { Footnote, Panel } from './Shell';
import { monoPlain } from '../theme';
import { useT } from '../i18n';

export function Devices() {
  const t = useT();
  const { Button, Icon } = ds();
  const [devices, setDevices] = useState<DeviceWire[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    try {
      const res = await api.vault.devices.list();
      setDevices(res.devices);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your devices.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);


  const revoke = async (d: DeviceWire) => {
    setBusy(d.id);
    setError(null);
    try {
      const res = await api.vault.devices.revoke(d.id);
      setNote(res.note);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not revoke that device.');
    } finally {
      setBusy(null);
    }
  };

  const pending = devices.filter((d) => d.state === 'pending');
  const active = devices.filter((d) => d.state === 'active');

  return (
    <Panel style={{ padding: '18px 20px', display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="monitor" size={16} />
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>
          {t('devices.apps')}
        </span>
        <span style={{ ...monoPlain, marginLeft: 'auto' }}>{active.length}</span>
      </div>

      {pending.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {pending.map((d) => (
            <div
              key={d.id}
              style={{
                display: 'grid',
                gap: 8,
                padding: '12px 14px',
                borderRadius: 12,
                background: 'var(--surface-sunken)',
                border: '1px solid var(--border-subtle)'
              }}
            >
              <span style={{ fontSize: 13, color: 'var(--text-strong)' }}>
                {d.label ?? d.client ?? 'A device'} is waiting to be approved
              </span>

              <span style={monoPlain}>
                {d.scope === 'vault' ? t('devices.whole-vault') : t('devices.this-app')}
              </span>

              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {t('devices.finish-in-the-app')}
              </span>

              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy === d.id}
                  onClick={() => void revoke(d)}
                >
                  Refuse
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {active.length > 0 && (
        <div style={{ display: 'grid', gap: 6 }}>
          {active.map((d) => (
            <div
              key={d.id}
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
            >
              <span style={{ color: 'var(--text-strong)' }}>
                {d.label ?? d.client ?? 'A device'}
              </span>
              <span style={{ ...monoPlain, marginLeft: 'auto' }}>
                {d.lastUsedAt ? 'in use' : 'approved'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy === d.id}
                onClick={() => void revoke(d)}
              >
                Revoke
              </Button>
            </div>
          ))}
        </div>
      )}

      {devices.length === 0 && (
        <Footnote>
          {t('devices.no-apps-linked-yet-sign')}
        </Footnote>
      )}

      {note && <Footnote>{note}</Footnote>}
      {error && <Footnote>{error}</Footnote>}
    </Panel>
  );
}
